import { ProductDetail, PurchaseData } from "@/@types/purchase";
import siteConfig from "@/variables";
import React from "react";
import { safeSessionStorage } from "../utils/safeStorages";

export type CheckoutState = {
  products: ProductDetail[];
  setProducts: (products: ProductDetail[]) => void;
  balance: number;
  setBalance: (balance: number) => void;
  affiliateName: string;
  setAffiliate: (affiliateName: string) => void;
  startCheckout: (locale: string, purchaseData: PurchaseData) => void;
  complete: CallableFunction;
  completed: boolean;
  clear: CallableFunction;
  loading?: boolean;
};

export const CheckoutInitialState = {
  products: [],
  setProducts: () => void 0,
  balance: 0,
  setBalance: () => void 0,
  affiliateName: "",
  setAffiliate: () => void 0,
  startCheckout: () => void 0,
  complete: () => void 0,
  completed: false,
  clear: () => void 0,
};

const CheckoutContext = React.createContext<CheckoutState>(CheckoutInitialState);

export const useCheckout = () => React.useContext(CheckoutContext);

export const CheckoutProvider = ({ children }: { children?: React.ReactNode }) => {
  const raw = safeSessionStorage.getItem(siteConfig.KEY_CHECKOUT_CONTEXT);
  const parsed = raw && JSON.parse(raw);
  const [products, setProducts] = React.useState<ProductDetail[]>(parsed?.products ?? []);
  const [balance, setBalance] = React.useState(parsed?.balance ?? 0);
  const [affiliateName, setAffiliate] = React.useState(parsed?.affiliateName ?? "");
  const [completed, setComplete] = React.useState(false);

  // We save the state to the session storage as a fallback which supports a player if they want to reload the page
  // The reason we use session here and not local, is because session is not shared across tabs, and is lost when you close the tab / browser
  React.useEffect(() => {
    safeSessionStorage.setItem(
      siteConfig.KEY_CHECKOUT_CONTEXT,
      JSON.stringify({ products, balance, affiliateName, completed }),
    );
  }, [products, balance, affiliateName, completed]);

  const startCheckout = React.useCallback(
    (locale: string, purchaseData: PurchaseData) => {
      if (purchaseData) {
        const { balance, products, affiliateName } = purchaseData;
        setBalance(balance ?? 0);
        setProducts(products);
        affiliateName && setAffiliate(affiliateName);
      } else if (!products.length || !balance) {
        return false;
      }
      // If we use navigate here we unmount the riotbar footer and its not replaced again once the page is loaded.
      // By triggering a full page change we force the riotbar to reload, which causes the footer to load.
      // TODO: When we can refresh / rerender the footer we should use navigate again.
      window.location.href = `/${locale}/checkout`;
      // navigate(`/${locale}/checkout`)
    },
    [products, balance, affiliateName],
  );

  /**
   * This is a helper function that marks the checkout as complete, and removes the items from storage.
   * From here we can check the complete value on next page load and clear out the values from product, balance, affiliate.
   *
   * We do this on the next page load, because if we cleared out the entries too soon, we'd cause a page redirect on checkout.
   */
  const complete = React.useCallback(() => {
    setComplete(true); // TODO: Reducer?
    safeSessionStorage.removeItem(siteConfig.KEY_CHECKOUT_CONTEXT);
  }, []);

  /**
   * This is a helper that just clears out all the internal state.
   */
  const clear = () => {
    setProducts([]);
    setBalance(0);
    setAffiliate("");
  };

  return (
    <CheckoutContext.Provider
      value={{
        products,
        setProducts,
        balance,
        setBalance,
        affiliateName,
        setAffiliate,
        startCheckout,
        complete,
        completed,
        clear,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};
